import { ApiError } from './api/client';

/** Full name from a client-like record. */
export function fullName(c: { firstName: string; lastName: string }): string {
  return `${c.firstName} ${c.lastName}`.trim();
}

/** Human date, e.g. "2 Jul 2026". */
export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Human date + time, e.g. "2 Jul 2026, 14:05". */
export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// IT/ES are Blissful Intelligence Report-only, but a report's stored
// language still needs a correct label wherever it's displayed.
const LANGUAGE_LABELS: Record<string, string> = {
  EN: 'English',
  SI: 'Sinhala',
  TA: 'Tamil',
  IT: 'Italian',
  ES: 'Spanish',
};
export function languageLabel(code: string): string {
  return LANGUAGE_LABELS[code] ?? code;
}

const CLIENT_TYPE_LABELS: Record<string, string> = { LOCAL: 'Local', FOREIGN: 'Foreign' };
export function clientTypeLabel(code: string): string {
  return CLIENT_TYPE_LABELS[code] ?? code;
}

const TITLE_LABELS: Record<string, string> = {
  MR: 'Mr',
  MRS: 'Mrs',
  MS: 'Ms',
  MISS: 'Miss',
  DR: 'Dr',
  REV: 'Rev',
  PROF: 'Prof',
};
export function titleLabel(code: string | null): string {
  if (!code) return '';
  return TITLE_LABELS[code] ?? code;
}

const AGE_LABELS: Record<string, string> = {
  CHILD: 'Child',
  TEEN: 'Teen',
  ADULT: 'Adult',
  SENIOR: 'Senior',
};
export function ageLabel(code: string | null): string {
  if (!code) return '—';
  return AGE_LABELS[code] ?? code;
}

const RELATION_LABELS: Record<string, string> = {
  MOTHER: 'Mother',
  FATHER: 'Father',
  GUARDIAN: 'Guardian',
  SIBLING: 'Sibling',
  SPOUSE: 'Spouse',
  OTHER: 'Other',
};
export function relationLabel(code: string): string {
  return RELATION_LABELS[code] ?? code;
}

/**
 * Strips everything but digits and a leading `+` so a naturally-typed number
 * (spaces, dashes, parens) matches the API's `phoneSchema` (`+?\d{7,15}`).
 */
export function normalizePhone(input: string): string {
  const trimmed = input.trim();
  const plus = trimmed.startsWith('+') ? '+' : '';
  return plus + trimmed.replace(/\D/g, '');
}

// Known report types get a friendly label; any future type (free-text,
// P13-T02/DEC-0058) falls back to a humanized form of its raw value.
const REPORT_TYPE_LABELS: Record<string, string> = {
  dmit: 'DMIT',
  'brain-skills': 'Blissful Intelligence Report',
};
export function reportTypeLabel(reportType: string): string {
  return (
    REPORT_TYPE_LABELS[reportType] ??
    reportType
      .split(/[-_]/)
      .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(' ')
  );
}

/** Best-effort human message from any thrown value. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}
